import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Quote, Star, User, ChevronLeft, ChevronRight } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

const TestimonialsSection = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'start' },
    [Autoplay({ delay: 5000, stopOnInteraction: false })]
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const testimonials = [
    {
      name: 'Maria Santos',
      role: 'Data Contributor',
      quote: 'Singularity Labs made it easy to contribute data ethically while earning real rewards. The arcade-style interface makes it fun!',
      rating: 5,
    },
    {
      name: 'Juan Dela Cruz',
      role: 'Side Quester',
      quote: 'I completed several side quests and the payout was fast and fair. Great platform for students like me.',
      rating: 5,
    },
    {
      name: 'Ana Reyes',
      role: 'Top Contributor',
      quote: 'Finally, a platform that values our data and compensates us properly. The leaderboard keeps me motivated!',
      rating: 5,
    },
    {
      name: 'Mark Gonzales',
      role: 'Power User',
      quote: 'The side quests are creative and the rewards are real. I have recommended Singularity Labs to all my classmates!',
      rating: 5,
    },
    {
      name: 'Lisa Tan',
      role: 'Verified Contributor',
      quote: 'Easy to use, fast payouts, and the support team is responsive. What more could you ask for?',
      rating: 5,
    },
  ];

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index) => emblaApi && emblaApi.scrollTo(index), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <Quote className="w-6 h-6 text-trophy-gold" />
            <h2 className="font-pixel text-lg sm:text-xl md:text-2xl text-foreground">
              TESTIMONIALS
            </h2>
            <Quote className="w-6 h-6 text-trophy-gold" />
          </div>
          <p className="font-terminal text-lg sm:text-xl text-lavender max-w-2xl mx-auto">
            Hear from our <span className="text-success-green">community</span> of data contributors and side questers.
          </p>
        </motion.div>

        {/* Carousel Container */}
        <div className="flex items-center gap-4">
          {/* Left Arrow */}
          <motion.button
            onClick={scrollPrev}
            className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 arcade-panel rounded-lg flex items-center justify-center border-2 border-neon-pink/50 hover:border-neon-pink transition-all"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-neon-pink" />
          </motion.button>

          {/* Embla Carousel */}
          <div className="overflow-hidden flex-1" ref={emblaRef}>
            <div className="flex">
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className="flex-[0_0_100%] min-w-0 sm:flex-[0_0_50%] lg:flex-[0_0_33.333%] px-3"
                >
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -5 }}
                    className="arcade-panel rounded-lg p-6 h-full border-l-4 border-trophy-gold"
                  >
                    {/* Quote Icon */}
                    <Quote className="w-8 h-8 text-neon-pink mb-4" />

                    {/* Quote Text */}
                    <p className="font-terminal text-base text-lavender mb-6 leading-relaxed min-h-[80px]">
                      "{testimonial.quote}"
                    </p>

                    {/* Rating */}
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-trophy-gold fill-trophy-gold" />
                      ))}
                    </div>

                    {/* Author */}
                    <div className="flex items-center gap-3 pt-4 border-t border-panel-purple">
                      <div className="w-10 h-10 bg-neon-pink/20 rounded-lg flex items-center justify-center border border-neon-pink/50">
                        <User className="w-5 h-5 text-neon-pink" />
                      </div>
                      <div>
                        <h4 className="font-pixel text-[10px] text-foreground">
                          {testimonial.name}
                        </h4>
                        <p className="font-terminal text-sm text-lavender">
                          {testimonial.role}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Arrow */}
          <motion.button
            onClick={scrollNext}
            className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 arcade-panel rounded-lg flex items-center justify-center border-2 border-neon-pink/50 hover:border-neon-pink transition-all"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-neon-pink" />
          </motion.button>
        </div>

        {/* Dot indicators */}
        <div className="flex justify-center gap-2 mt-8">
          {scrollSnaps.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollTo(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === selectedIndex
                  ? 'bg-neon-pink w-6'
                  : 'bg-panel-purple w-2 hover:bg-lavender/50'
              }`}
            />
          ))}
        </div>

        {/* Counter */}
        <div className="text-center mt-4">
          <span className="font-pixel text-[10px] text-lavender">
            <span className="text-neon-pink">{selectedIndex + 1}</span> / {testimonials.length}
          </span>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
